/**
 * Builds the create body for a child competition (division / conference /
 * season) under an existing schedule.
 *
 * Mirrors the legacy Setup tab's handler
 * (`web/src/pages/Schedule/ScheduleTree/index.jsx`, `onSubmit`) field for field,
 * so the two tabs POST identical bodies while both exist. Endpoint:
 * `POST /organizations/:orgId/schedules`.
 */

/** Raw values as the legacy form emits them (untyped JS form config). */
export interface DivisionFormValues {
  /** Name — the form calls it `title`. */
  title?: string
  /** Type — the form calls it `subtitle`: 'Season' | 'Conference' | 'Division'. */
  subtitle?: string
  sort_order?: number | string | null
  is_visible_standings?: boolean
  /** ISO datetime or date; sent date-only. */
  lock_roster_date?: string | null
  starts_at?: string | null
  /** Salesforce ID (Canlan). */
  import_2_id?: string | null
  game_cutoff?: number | string | null
  // Registration/Salesforce block — only sent for a Division when enabled.
  day_of_week?: string | null
  deposit?: number | string | null
  division_age?: string | null
  division_gender?: string | null
  division_group?: string | null
  team_cost?: number | string | null
  is_active?: boolean
  season_type?: string | null
  online_registration_open?: boolean
  season_currency?: string | null
  tax_rate?: number | string | null
}

export interface DivisionParent {
  scheduleId: string
  startsAt: string | null
  endsAt: string | null
}

export interface DivisionPayload {
  [key: string]: unknown
}

/** Date-only, as the API expects; null when unset. */
const dateOnly = (value: string | null | undefined): string | null =>
  value ? value.split('T')[0] : null

const hasKey = (values: object, key: string): boolean =>
  Object.prototype.hasOwnProperty.call(values, key)

export const toDivisionPayload = ({
  values,
  parent,
  parentId,
  isPublic,
  showSalesforceSettings = false,
}: {
  values: DivisionFormValues
  parent: DivisionParent
  /**
   * Where it is nested — the clicked node, not necessarily the root. CREATE
   * only: an edit (`PUT /schedules/:id`) must not resend it, matching legacy.
   */
  parentId?: string
  /**
   * The owning ORGANIZATION's is_public, which a new child inherits. CREATE
   * only, for the same reason.
   */
  isPublic?: boolean
  showSalesforceSettings?: boolean
}): DivisionPayload => {
  const payload: DivisionPayload = {
    type: values.subtitle,
    name: values.title,
    name_full: values.title,
    // Dates are inherited from the parent competition rather than entered.
    starts_at: parent.startsAt,
    ends_at: parent.endsAt,
    sort_order: values.sort_order,
    is_visible_standings: values.is_visible_standings,
    lock_roster_date: dateOnly(values.lock_roster_date),
  }

  // Legacy quirk, preserved: when the form omits the key ENTIRELY (as opposed to
  // sending it empty), the competition's own start date is used instead.
  if (!hasKey(values, 'lock_roster_date')) {
    payload.lock_roster_date = values.starts_at
  }

  if (values.subtitle === 'Division' && showSalesforceSettings) {
    payload.day_of_week = values.day_of_week
    payload.deposit = values.deposit
    payload.division_age = values.division_age
    payload.division_gender = values.division_gender
    payload.division_group = values.division_group
    payload.team_cost = values.team_cost
    payload.is_active = values.is_active
    payload.season_type = values.season_type
    payload.online_registration_open = values.online_registration_open
    payload.season_currency = values.season_currency
    payload.tax_rate = values.tax_rate
  }

  if (hasKey(values, 'game_cutoff')) {
    payload.settings = {
      game_cutoff: parseInt(String(values.game_cutoff), 10),
    }
  }

  if (values.import_2_id) {
    payload.import_2_id = values.import_2_id
  }

  // Create-only fields. An edit PUTs to the node's own URL, so re-sending
  // parent_id would risk re-parenting it and is_public would overwrite the
  // competition's own visibility — legacy sends neither on edit.
  if (parentId !== undefined) {
    payload.parent_id = parentId
  }
  if (isPublic !== undefined) {
    payload.is_public = isPublic
  }

  return payload
}
